import { useCallback, useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { toast } from "sonner";
import { CalendarDays, Megaphone } from "lucide-react";
import { api, errMsg } from "@/lib/api";
import { TypeBadge } from "@/components/jobs/Badges";

const dayName = (d) => new Date(`${d}T12:00`).toLocaleDateString("en-GB", { weekday: "short", day: "numeric", month: "short" });

export const AskCover = ({ jobId, req, onDone, where = "week" }) => {
  const [busy, setBusy] = useState(false);
  const go = async () => {
    setBusy(true);
    try { const { data } = await api.post(`/jobs/${jobId}/cover-request`); toast.success(`Asked ${data.sent_to} rider${data.sent_to === 1 ? "" : "s"} to cover`); onDone?.(); }
    catch (e) { toast.error(errMsg(e)); }
    setBusy(false);
  };
  return (
    <button onClick={go} disabled={busy} data-testid={where === "week" ? `ask-cover-${jobId}` : `gap-ask-cover-${jobId}`} className="inline-flex max-w-full items-center gap-1 rounded-full text-left bg-amber-500 px-3 py-1 text-xs font-bold text-black disabled:opacity-50">
      <Megaphone className="h-3 w-3" />{req ? "Ask again" : "Ask riders"}
    </button>
  );
};

const JobRow = ({ j, manage, reload }) => {
  const req = j.cover_request;
  const claimed = req?.status === "claimed";
  return (
    <li className={`rounded-lg border-l-4 px-2 py-1.5 text-xs ${j.covered ? "border-l-emerald-500 bg-emerald-500/5" : "border-l-amber-500 bg-amber-500/10"}`} data-testid={`week-job-${j.id}`}>
      <div className="flex flex-wrap items-center gap-1.5"><b className="font-mono">{j.scheduled_for.slice(11, 16)}</b><Link to={`/jobs/${j.id}`} className="font-mono font-bold underline-offset-2 hover:underline">{j.ref}</Link><TypeBadge t={j.job_type} /></div>
      <p className="mt-0.5 truncate font-semibold">{j.pickup} → {j.dropoff}</p>
      <p className="text-muted-foreground" data-testid={`week-job-cover-${j.id}`}>{j.covered ? `On rota: ${j.on_rota.join(", ")}` : "Nobody on the rota"}{j.rider_name ? ` · assigned ${j.rider_name}` : ""}</p>
      {!j.covered && (
        <div className="mt-1 flex flex-wrap items-center gap-2">
          {req?.status === "open" && <span className="font-semibold text-amber-500" data-testid={`week-job-asked-${j.id}`}>Asked {req.sent_to} rider{req.sent_to === 1 ? "" : "s"} · waiting</span>}
          {manage && <AskCover jobId={j.id} req={req} onDone={reload} />}
        </div>
      )}
      {claimed && <p className="font-semibold text-emerald-500">Covered by {req.claimed_by_name}</p>}
    </li>
  );
};

const Day = ({ d, manage, reload }) => (
  <div className={`min-w-0 space-y-2 rounded-xl border p-3 ${d.gaps ? "border-amber-500" : ""}`} data-testid={`week-day-${d.date}`}>
    <div className="flex items-center justify-between gap-2"><p className="font-bold">{dayName(d.date)}</p>
      {d.gaps > 0 ? <span className="rounded-full bg-amber-500 px-2 py-0.5 text-xs font-black text-black">{d.gaps} gap{d.gaps > 1 ? "s" : ""}</span> : d.jobs.length > 0 && <span className="text-xs font-bold text-emerald-500">All covered</span>}</div>
    <div className="flex flex-wrap gap-1">{d.shifts.map((s) => (
      <span key={s.name} className={`rounded px-1.5 py-0.5 text-[11px] font-semibold ${s.filled === 0 ? "bg-red-500/15 text-red-500" : s.filled < s.needed ? "bg-amber-500/15 text-amber-500" : "bg-emerald-500/15 text-emerald-500"}`}>{s.name} {s.start}-{s.end} · {s.filled}/{s.needed}</span>
    ))}{d.shifts.length === 0 && <span className="text-[11px] text-muted-foreground">No shifts set up</span>}</div>
    {d.jobs.length === 0 ? <p className="text-xs text-muted-foreground">No pre-booked jobs</p> : <ul className="space-y-1.5">{d.jobs.map((j) => <JobRow key={j.id} j={j} manage={manage} reload={reload} />)}</ul>}
  </div>
);

export const WeekAhead = ({ manage, tick }) => {
  const [w, setW] = useState(null);
  const load = useCallback(() => api.get("/jobs/lookahead?days=7").then((r) => setW(r.data)).catch(() => {}), []);
  useEffect(() => { load(); const t = setInterval(load, 30000); return () => clearInterval(t); }, [load, tick]);
  if (!w) return <div className="h-40 animate-pulse rounded-2xl bg-card" />;
  return (
    <section className="space-y-3 rounded-2xl border bg-card p-4" data-testid="week-ahead">
      <div className="flex flex-wrap items-center justify-between gap-2">
        <h2 className="flex items-center gap-2 font-bold"><CalendarDays className="h-4 w-4" /> Next 7 days · pre-booked vs rota</h2>
        <div className="flex items-center gap-2 text-sm"><span data-testid="week-ahead-summary" className={w.gaps ? "font-bold text-amber-500" : "text-muted-foreground"}>{w.jobs} job{w.jobs === 1 ? "" : "s"} · {w.gaps} gap{w.gaps === 1 ? "" : "s"}</span>
          <Link to="/rota" className="rounded-full border px-3 py-1 text-xs font-bold" data-testid="week-ahead-rota-link">Open rota</Link></div>
      </div>
      <div className="grid gap-3 sm:grid-cols-2 lg:grid-cols-4 xl:grid-cols-7">{w.days.map((d) => <Day key={d.date} d={d} manage={manage} reload={load} />)}</div>
    </section>
  );
};
